import { useEffect, useId, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Header } from './Header.jsx';
import { MobileNav } from './MobileNav.jsx';
import { Footer } from './Footer.jsx';
import { HeaderHeightContext } from './headerContext.js';
import styles from './Layout.module.css';

/**
 * The shell every page renders inside: header, mobile menu, main landmark,
 * footer. Previously each of the six HTML files carried its own copy of all
 * of this, which is how the footer heading level and the nav markup drifted
 * apart between pages.
 */
export function Layout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const headerRef = useRef(null);
  const toggleRef = useRef(null);
  const mainRef = useRef(null);
  const location = useLocation();

  const closeMenu = () => {
    setMenuOpen(false);
    // Focus belongs back on the control that opened the panel.
    toggleRef.current?.focus();
  };

  /* A route change in a single-page app moves no focus and announces
     nothing on its own. Sending focus to the main landmark restores what a
     full page load used to do for a screen-reader or keyboard visitor.

     preventScroll matters: focusing an element scrolls it into view, and
     <main> begins below the sticky header — so without it every navigation
     nudged the page down by exactly the header's height, landing the
     visitor slightly scrolled on a page they had only just opened.

     Guarded on the pathname rather than a first-render boolean. A boolean
     flag is not safe under StrictMode, where the effect runs, is cleaned
     up and runs again: the first pass flipped the flag and returned, and
     the second sailed straight past it and focused on initial load. */
  const handledPath = useRef(location.pathname);
  useEffect(() => {
    if (handledPath.current === location.pathname) return;
    handledPath.current = location.pathname;
    setMenuOpen(false);
    mainRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);

  return (
    <HeaderHeightContext.Provider value={headerRef}>
      <a className={styles.skipLink} href="#main">Skip to content</a>

      <Header
        headerRef={headerRef}
        toggleRef={toggleRef}
        menuId={menuId}
        menuOpen={menuOpen}
        onOpenMenu={() => setMenuOpen(true)}
      />

      <MobileNav id={menuId} open={menuOpen} onClose={closeMenu} />

      <main id="main" ref={mainRef} className={styles.main} tabIndex={-1}>
        <Outlet />
      </main>

      <Footer />
    </HeaderHeightContext.Provider>
  );
}
